import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import styles from './VoisinsCompare.module.css';

/**
 * VoisinsCompare — comparatif des restaurants voisins.
 * L'élément `you: true` est mis en avant en carte noire avec badge "VOUS".
 *
 * Props :
 *  - title       : "Vos voisins du Vieux-Port"
 *  - neighbours  : [{ name, rating, count, you?, emoji? }] | [] | null | undefined
 *                  `emoji` est optionnel — si absent, rien n'est rendu à sa place
 *                  (rétrocompat avec d'anciens consumers qui ne fournissent pas
 *                  l'emoji). Le mapping cuisine/type Google se fait côté caller
 *                  via `utils/concurrentEmoji.pickConcurrentEmoji`.
 *
 * ID préservé : #voisins-compare
 *
 * ⚠️ Hotfix 04/05/2026 — Les mocks DEFAULT_NEIGHBOURS (Chez Marie, L'Estaminet,
 * Le Bistrot du Coin, La Bouillabaisse, Pizzeria Roma) ont été retirés. Si la
 * prop `neighbours` est vide / absente, on affiche un empty state plutôt que
 * de fausses données qui n'ont aucun rapport avec les vrais voisins du resto.
 */
export default function VoisinsCompare({
  title,
  neighbours,
}) {
  const { t } = useTranslation('dashboard');
  const safeTitle = title || t('voisins_compare.default_title');
  const hasData = Array.isArray(neighbours) && neighbours.length > 0;

  return (
    <Card span={12} id="voisins-compare">
      <h2 className={styles.title}>
        <span className={styles.icon}>📍</span> {safeTitle}
      </h2>
      {hasData ? (
        <div className={styles.grid}>
          {neighbours.map((n, i) => (
            <div
              key={i}
              className={`${styles.item} ${n.you ? styles.you : ''}`}
            >
              {n.you && <span className={styles.youBadge}>{t('voisins_compare.badge_you')}</span>}
              {n.emoji && <div className={styles.itemEmoji} aria-hidden="true">{n.emoji}</div>}
              <div className={styles.name} id={n.you ? 'comp-your-name' : undefined}>{n.name}</div>
              <div className={styles.rating} id={n.you ? 'comp-your-note' : undefined}>
                <span className={styles.star}>★</span>{n.rating}
              </div>
              <div className={styles.count} id={n.you ? 'comp-your-count' : undefined}>{n.count}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          <span className={styles.emptyEmoji}>{t('voisins_compare.empty_emoji')}</span>
          <span className={styles.emptyTitle}>{t('voisins_compare.empty_title')}</span>
          <span className={styles.emptyHint}>
            {t('voisins_compare.empty_hint')}
          </span>
        </div>
      )}
    </Card>
  );
}
